import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@strands/ui/components/alert-dialog";

interface FolderConflictDialogProps {
  /** Number of files already in the session workspace, or null when there's
   *  no conflict to resolve (dialog stays closed). */
  serverCount: number | null;
  onCancel: () => void;
  /** Keep both sides: pull the session's files into the folder, then sync
   *  both ways. Nothing is deleted. Maps to finishConnect(handle, "server"). */
  onMerge: () => void;
  /** Wipe the session workspace and keep only the folder's contents. Maps to
   *  finishConnect(handle, "local"). */
  onReplace: () => void;
}

/** «1 файл» / «2 файла» / «5 файлов» — Russian count agreement. */
function pluralFiles(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return `${n} файл`;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return `${n} файла`;
  return `${n} файлов`;
}

/**
 * Shown when the user connects a local folder to a session whose workspace
 * already holds files (e.g. a document the agent created). useDocSync.pickFolder
 * raises this instead of silently picking a side — without it the connect was a
 * dead-end (`conflict` was set but never rendered).
 *
 * Two self-describing option cards rather than loud filled buttons, so the
 * destructive choice doesn't out-shout the safe one.
 */
export function FolderConflictDialog({ serverCount, onCancel, onMerge, onReplace }: FolderConflictDialogProps) {
  return (
    <AlertDialog open={serverCount !== null} onOpenChange={(v) => !v && onCancel()}>
      <AlertDialogContent className="gap-0 rounded-2xl p-6 shadow-[var(--shadow-02)] ring-0 max-w-[26rem]! sm:max-w-[26rem]!">
        <AlertDialogHeader className="text-left">
          <AlertDialogTitle className="text-[15px] font-semibold tracking-[-0.2px] text-foreground">
            Подключить локальную папку
          </AlertDialogTitle>
          <AlertDialogDescription className="mt-1.5 text-[13px] leading-5 text-foreground-muted text-wrap!">
            В рабочей области этой сессии уже есть {pluralFiles(serverCount ?? 0)}. Выберите, что с ними сделать.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="mt-4 flex flex-col gap-2">
          <button
            type="button"
            onClick={onMerge}
            className="group flex w-full flex-col items-start gap-0.5 rounded-xl border border-input bg-background px-3.5 py-3 text-left outline-none transition-colors hover:bg-muted focus-visible:shadow-[0_0_0_3px_var(--color-focus-ring)]"
          >
            <span className="text-[13.5px] font-medium text-foreground">Объединить</span>
            <span className="text-[12px] leading-snug text-foreground-muted">
              Файлы сессии добавятся в папку и продолжат синхронизироваться в обе стороны. Ничего не удаляется.
            </span>
          </button>

          <button
            type="button"
            onClick={onReplace}
            className="group flex w-full flex-col items-start gap-0.5 rounded-xl border border-input bg-background px-3.5 py-3 text-left outline-none transition-colors hover:border-destructive/30 hover:bg-destructive/5 focus-visible:shadow-[0_0_0_3px_var(--color-focus-ring-error)]"
          >
            <span className="text-[13.5px] font-medium text-foreground">Заменить содержимым папки</span>
            <span className="text-[12px] leading-snug text-foreground-muted">
              Файлы сессии будут удалены — останется только то, что лежит в папке.
            </span>
          </button>
        </div>

        <AlertDialogFooter className="mx-0! mb-0! mt-3 flex-row justify-end border-t-0 bg-transparent p-0">
          <AlertDialogCancel variant="ghost" className="rounded-xl px-3.5 py-2" onClick={onCancel}>
            Отмена
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
